import { type CareOrder, isCompound, type QtyInputCurrencyOptions, type Security } from '@talos/kyoko';
import type { WritableDraft } from 'immer';
import { compact, uniq, values } from 'lodash-es';
import { UNITS_CURRENCY } from '../CareOrderFormView/types';
import type { RFQCurrency, RFQState } from './types';

/**
 * An interface defining the behavior of our different currency "modes" in the RFQ Form.
 * Depending on the different _types_ of securities we can select in the form, there is different behavior around which currencies
 * to default to, which currencies can be selected, etc. This interface collects this logic in one place.
 */
interface RFQFormCurrencySpec {
  type: 'compound' | 'default';
  /** Used to figure out which currency mode to use for the given security. */
  isMyType: undefined | ((security: Security) => boolean);
  /** To be called when the symbol is changed. Ensures that the currency is set correctly in the state. */
  onSymbolChanged: (state: WritableDraft<RFQState>, security: Security) => void;
  /** Given a security, returns the currency options to provide the user */
  getAvailableCurrencies: (security: Security) => QtyInputCurrencyOptions<RFQCurrency>[];
}

export const RFQFormCurrencies: { [key in RFQFormCurrencySpec['type']]: RFQFormCurrencySpec } = {
  /** Default is for spot and falconx options currently. We support Base and Quote for both these types of instruments. */
  default: {
    type: 'default',
    isMyType: undefined, // default is handled explicitly in getRFQFormCurrencySpec below for simpler typing instead of just returning true here
    onSymbolChanged: (state, security) => {
      const currentSelectionNotValid =
        state.form.rfqCurrencyField.value !== security.BaseCurrency &&
        state.form.rfqCurrencyField.value !== security.QuoteCurrency;
      if (currentSelectionNotValid) {
        state.form.rfqCurrencyField = state.form.rfqCurrencyField.updateValue(security.BaseCurrency, true);
      }
    },
    getAvailableCurrencies: security => {
      return uniq(compact([security.BaseCurrency, security.QuoteCurrency])).map(currency => ({
        currency,
        label: currency,
      }));
    },
  },
  compound: {
    type: 'compound',
    isMyType: security => isCompound(security) ?? false,
    onSymbolChanged: (state, security) => {
      state.form.rfqCurrencyField = state.form.rfqCurrencyField.updateValue(UNITS_CURRENCY);
    },
    getAvailableCurrencies: security => {
      return [{ currency: UNITS_CURRENCY, label: 'Units' }];
    },
  },
} as const;

export function careOrderCurrencyToRFQSliceCurrency(coCurrency: CareOrder['Currency']): RFQCurrency {
  return coCurrency == null ? UNITS_CURRENCY : coCurrency;
}

export function getRFQFormCurrencySpec(security: Security): RFQFormCurrencySpec {
  const spec = values(RFQFormCurrencies).find(spec => spec.isMyType?.(security));
  return spec ?? RFQFormCurrencies.default;
}
